import React, { useMemo, useState } from 'react';
import { Plots, errorMessage } from '../../api/client';
import { Plot, PlotMember, PlotMemberList, PlotRef } from '../../types';
import { Icon } from '../ui/Icon';
import { Confirm, Empty, Loading, Note, PlayerHead, Section } from '../ui/primitives';
import { EVERYONE, isEveryone, memberName } from '../../utils/plots';

interface Props {
  plotRef: PlotRef;
  plot: Plot;
  trusted: PlotMember[] | null;
  members: PlotMember[] | null;
  error: string | null;
  onTrustedChange: (list: PlotMember[]) => void;
  onMembersChange: (list: PlotMember[]) => void;
  /** Adding to one list takes the player off the other, so both are refetched afterwards. */
  onReload: () => void;
}

const PLAYER_RE = /^([A-Za-z0-9_]{1,16}|\*)$/;

interface ListProps {
  plotRef: PlotRef;
  plot: Plot;
  list: PlotMemberList;
  title: string;
  blurb: string;
  verb: string;
  entries: PlotMember[] | null;
  error: string | null;
  onChange: (list: PlotMember[]) => void;
  onReload: () => void;
}

/** One of the plot's player lists: add by username (or * for everyone), remove with confirmation. */
const MemberList: React.FC<ListProps> = ({ plotRef, plot, list, title, blurb, verb, entries, error, onChange, onReload }) => {
  const [input, setInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<PlotMember | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const sorted = useMemo(
    () =>
      [...(entries || [])].sort((a, b) => {
        if (isEveryone(a) !== isEveryone(b)) return isEveryone(a) ? -1 : 1;
        return memberName(a).localeCompare(memberName(b));
      }),
    [entries]
  );
  const valid = PLAYER_RE.test(input.trim());

  const add = async () => {
    const player = input.trim();
    if (!valid || adding) return;
    setAdding(true);
    setAddError(null);
    setJustAdded(null);
    try {
      const added = await Plots.addMember(plotRef, list, player);
      onChange([...(entries || []).filter((m) => m.uuid !== added.uuid), added]);
      setInput('');
      setJustAdded(memberName(added));
      onReload();
    } catch (e) {
      setAddError(errorMessage(e, `Could not ${verb} ${player}. Please try again.`));
    } finally {
      setAdding(false);
    }
  };

  const remove = async () => {
    if (!pendingRemove) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await Plots.removeMember(plotRef, list, isEveryone(pendingRemove) ? EVERYONE : pendingRemove.uuid);
      onChange((entries || []).filter((m) => m.uuid !== pendingRemove.uuid));
      setPendingRemove(null);
    } catch (e) {
      setRemoveError(errorMessage(e, 'Could not remove that player. Please try again.'));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Section title={title} blurb={blurb}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="input input--mono"
            placeholder="minecraft username, or *"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setAddError(null);
              setJustAdded(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            style={{ width: 260 }}
            maxLength={16}
            disabled={adding}
            aria-label={`Minecraft username to ${verb}`}
          />
          <button type="button" className="btn btn--primary" onClick={add} disabled={!valid || adding}>
            {adding ? <span className="spin" style={{ borderTopColor: '#fff' }} /> : <Icon name="plus" size={12} />} {verb[0].toUpperCase() + verb.slice(1)} player
          </button>
          {input && !valid && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Usernames are 1–16 letters, numbers or underscores, or * for everyone.</span>}
        </div>
        <small style={{ display: 'block', marginTop: 8, fontSize: 11.5, color: 'var(--ink3)' }}>
          Same as <span className="kbd">/plot {list === 'trusted' ? 'trust' : 'add'}</span> in-game. Type <span className="kbd">*</span> to open the plot to everyone.
        </small>
        {addError && (
          <div style={{ marginTop: 12 }}>
            <Note tone="danger">{addError}</Note>
          </div>
        )}
        {justAdded && !addError && (
          <div style={{ marginTop: 12 }}>
            <Note tone="ok">
              {justAdded} is now {list === 'trusted' ? 'trusted' : 'added'} on plot {plot.plotId}.
            </Note>
          </div>
        )}
      </div>

      {error && <Note tone="danger">{error}</Note>}
      {entries == null && !error ? (
        <Loading />
      ) : sorted.length === 0 ? (
        <Empty icon="users" title={list === 'trusted' ? 'No trusted players yet' : 'No added players yet'}>
          {list === 'trusted' ? 'Trust friends so they can build with you whenever they like.' : 'Add players who may build while you are online.'}
        </Empty>
      ) : (
        <div className="heads">
          {sorted.map((m) => (
            <span className="hcard" key={m.uuid} title={isEveryone(m) ? 'Everyone on the server' : m.uuid}>
              {isEveryone(m) ? (
                <span className="bkico" style={{ width: 26, height: 26 }}>
                  <Icon name="users" size={14} />
                </span>
              ) : (
                <PlayerHead uuid={m.uuid} size={26} />
              )}
              {memberName(m)}
              <button type="button" className="x" title={`Remove ${memberName(m)}`} onClick={() => setPendingRemove(m)}>
                <Icon name="x" size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      <Confirm
        open={!!pendingRemove}
        title={pendingRemove ? `Remove ${memberName(pendingRemove)}?` : ''}
        body={
          pendingRemove && isEveryone(pendingRemove)
            ? `The plot is no longer open to everyone; only the players on your lists can build on plot ${plot.plotId}.`
            : `They can no longer build on plot ${plot.plotId}.`
        }
        confirmLabel="Remove"
        danger
        busy={removing}
        error={removeError}
        onCancel={() => {
          if (!removing) {
            setPendingRemove(null);
            setRemoveError(null);
          }
        }}
        onConfirm={remove}
      />
    </Section>
  );
};

/** Players tab: the plot's trusted (/plot trust) and added (/plot add) lists. */
const PlotMembersTab: React.FC<Props> = ({ plotRef, plot, trusted, members, error, onTrustedChange, onMembersChange, onReload }) => (
  <>
    <MemberList
      plotRef={plotRef}
      plot={plot}
      list="trusted"
      title="Trusted players"
      blurb="They can build on your plot at any time, even while you're offline."
      verb="trust"
      entries={trusted}
      error={error}
      onChange={onTrustedChange}
      onReload={onReload}
    />
    <MemberList
      plotRef={plotRef}
      plot={plot}
      list="members"
      title="Added players"
      blurb="They can build on your plot only while you're online."
      verb="add"
      entries={members}
      error={error}
      onChange={onMembersChange}
      onReload={onReload}
    />
  </>
);

export default PlotMembersTab;
